import { z } from 'zod'

// =============================================================================
// Schéma Zod du formulaire Appointment.
// Le calendrier est sélectionné via une chaîne "provider:id" puis dérivée en
// deux champs ; les sélecteurs natifs HTML obligent ce détour côté form.
// =============================================================================

/**
 * Normalise un numéro vers le format E.164 attendu par Twilio.
 * Retourne null si le numéro ne peut pas être normalisé proprement.
 * Hypothèse : pas d'indicatif → numéro nord-américain (+1).
 */
export function normalizePhone(raw: string): string | null {
  const cleaned = raw.replace(/["'\s\-(). ]/g, '')
  if (/^\+[1-9]\d{9,14}$/.test(cleaned)) return cleaned
  if (/^1\d{10}$/.test(cleaned)) return `+${cleaned}`
  if (/^\d{10}$/.test(cleaned)) return `+1${cleaned}`
  return null
}

export const appointmentFormSchema = z
  .object({
    /** Format "google:42" | "microsoft:7" — résolu en {provider,id} au submit. */
    calendar_selection: z.string().min(1, 'calendar_required'),

    client_name: z.string().trim().min(1, 'client_name_required'),
    client_phone: z.string().trim().min(1, 'client_phone_required'),
    client_email: z.string().trim(),

    /** YYYY-MM-DD. */
    start_date: z.string().min(1, 'date_required'),
    /** HH:MM. */
    start_time: z.string().min(1, 'time_required'),
    duration: z.number().int().min(5).max(480),

    notes: z.string(),
    appointment_type: z.enum(['phone', 'in_person', 'video']),
    location: z.string(),

    send_sms: z.boolean(),
    sms_from_number: z.string(),
    send_email: z.boolean(),
    send_calendar_invite: z.boolean(),
  })
  .superRefine((val, ctx) => {
    // Format téléphone E.164 — Twilio refuse tout ce qui n'est pas normalisable.
    if (normalizePhone(val.client_phone) === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['client_phone'],
        message: 'invalid_phone',
      })
    }
    // Email obligatoire si type=video (le lien Meet/Teams part par mail).
    if (val.appointment_type === 'video' && !val.client_email.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['client_email'],
        message: 'email_required_for_video',
      })
    }
    // Format email basique quand renseigné.
    if (val.client_email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.client_email.trim())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['client_email'],
        message: 'invalid_email',
      })
    }
  })

export type AppointmentFormValues = z.infer<typeof appointmentFormSchema>

export const APPOINTMENT_FORM_DEFAULTS: AppointmentFormValues = {
  calendar_selection: '',
  client_name: '',
  client_phone: '',
  client_email: '',
  start_date: '',
  start_time: '09:00',
  duration: 30,
  notes: '',
  appointment_type: 'phone',
  location: '',
  send_sms: false,
  sms_from_number: '',
  send_email: false,
  send_calendar_invite: false,
}

export const DURATION_OPTIONS: { value: number; label: string }[] = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '1h 00' },
  { value: 90, label: '1h 30' },
  { value: 120, label: '2h 00' },
]

/** Génère les créneaux 7h-21h par tranches de 15 min ("07:00" → "20:45"). */
export function buildTimeOptions(): string[] {
  const out: string[] = []
  for (let h = 7; h < 21; h++) {
    for (let m = 0; m < 60; m += 15) {
      out.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`)
    }
  }
  return out
}
